import { useEffect, useRef, useState, useMemo } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  IconButton,
  Alert,
  Tooltip,
} from "@mui/material";
import Layout from "../../layout/Layout";
import { transactionHistoryAPI } from "../../config";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";

import { MaterialReactTable } from "material-react-table";

import { apiData } from "../../services/api_service";
import { formatNumber, formatAED } from "../../utils/format";
import moment from "moment";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

function TransactionHistory() {
  const [totalCount, setTotalCount] = useState(0);
  const [transactionData, setTransactionData] = useState([]);

  const [loading, setLoading] = useState(false);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");

  let tempRefs = useRef(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (tempRefs.current === false) {
      fetchOrders(1);
    }
  }, []);

  const fetchOrders = async () => {
    const payload = {
      pageIndex: pageIndex,
      pageSize: pageSize,
      sorting: sorting?.length > 0 ? JSON.stringify(sorting[0]) : null,
      search: filters?.length > 0 ? JSON.stringify(filters) : null,
    };
    try {
      const response = await apiData("get", transactionHistoryAPI, payload);

      setTransactionData(response.data);
      setTotalCount(response.pagination);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [pageIndex, pageSize, filters, sorting]);

  const handleExportData = async () => {
    try {
      setLoading(true);

      // Prepare payload with all filters, sorting, search, but unlimited pageSize
      const payload = {
        pageIndex: 0,
        pageSize: 1000000, // effectively "all" records
        sorting: sorting?.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: filters?.length > 0 ? JSON.stringify(filters) : null,
      };

      const response = await apiData("get", transactionHistoryAPI, payload);

      if (!response?.data || response.data?.length === 0) {
        alert("No data to export!");
        return;
      }

      const numOrBlank = (v) =>
        v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? "" : Number(v);

      const exportData = response.data.map((item, index) => ({
        "#": index + 1,
        "Date / Time": item.transaction_datetime
          ? dayjs(item.transaction_datetime).format("DD-MM-YYYY HH:mm")
          : "",
        "Order ID": item.order_number || "",
        Store: item.store_name || "",
        SKU: item.product_sku || "",
        "Product Name": item.product_name || "",
        "Transaction Type": item.transaction_type || "",
        // A signed number (not "+5" text) so it can be summed in Excel.
        "Quantity Change (+/-)":
          item.transferred_from_id && item.transferred_store_id
            ? Number(item.quantity_change) || 0
            : item.transaction_type?.toLowerCase() === "deduct"
              ? -(Number(item.quantity_change) || 0)
              : Number(item.quantity_change) || 0,
        "In Stock": numOrBlank(item.Balance_instock),
        Notes: item.remarks || "",
        "Selling Price": numOrBlank(item.selling_price),
        "Total Price": numOrBlank(item.total_value),
        "Performed By": item.performed_by || "",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      ["Selling Price", "Total Price"].forEach((h) => {
        const c = Object.keys(exportData[0] || {}).indexOf(h);
        for (let r = 1; c >= 0 && r <= exportData.length; r += 1) {
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          if (cell && cell.t === "n") cell.z = '"AED" #,##0.00';
        }
      });
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "sales_order_history");

      const fileName = `Invoice_History_${moment().format("DD-MM-YYYY")}.xlsx`;
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "transaction_datetime",
        header: "Date / Time",
        size: 50,
        Cell: ({ cell }) => dayjs(cell.getValue()).format("DD-MM-YYYY"),
        Filter: ({ column }) => {
          const value = column.getFilterValue() || null;

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                value={value ? dayjs(value, "YYYY-MM-DD") : null}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = dayjs(newValue).format("YYYY-MM-DD");
                    column.setFilterValue(formatted);
                  } else {
                    column.setFilterValue(null);
                  }
                }}
                clearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="standard"
                    placeholder="Select Date"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#fff !important",
                        opacity: 0.7,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {value && (
                            <IconButton
                              size="small"
                              onClick={() => column.setFilterValue(null)}
                              sx={{ color: "#fff" }}
                            >
                              ✕
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        accessorKey: "order_number",
        header: "Order ID",
        size: 50,

        Cell: ({ row }) => {
          const num = row.original.order_number;
          const url = row.original.order_receipt;
          if (!num) return "N/A";
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", fontWeight: 600, textDecoration: "none" }}
            >
              {num}
            </a>
          ) : (
            num
          );
        },
      },
      {
        accessorKey: "store_name",
        header: "Store",
        size: 50,
      },
      {
        accessorKey: "product_sku",
        header: "SKU",
        size: 50,
      },
      {
        accessorKey: "product_name",
        header: "Product Name",
        size: 50,
      },
      {
        accessorKey: "transaction_type",
        header: "Transaction Type",
        size: 50,
        filterVariant: "select",
        filterSelectOptions: [
          { label: "Add", value: "add" },
          { label: "Deduct", value: "deduct" },
        ],
        Cell: ({ cell }) => {
          const style = {
            color:
              cell.row.original.transaction_type?.toLowerCase() === "deduct"
                ? "red"
                : "green",
            fontWeight: 600,
          };
          return (
            <span style={style}>{cell.row.original.transaction_type}</span>
          );
        },
      },
      {
        accessorKey: "quantity_change",
        header: "Quantity Change (+/-)",
        size: 50,
        Cell: ({ cell }) => {
          const value = cell.row.original.quantity_change; // or cell.getValue() if you mapped correctly
          const type = cell.row.original.transaction_type;

          const displayValue =
            type?.toLowerCase() === "deduct"
              ? `-${formatNumber(value)}`
              : `+${formatNumber(value)}`;

          const style = {
            color: type?.toLowerCase() === "deduct" ? "red" : "green",
            fontWeight: 600,
          };

          return <span style={style}>{displayValue}</span>;
        },
      },
      {
        accessorKey: "Balance_instock",
        header: "In Stock",
        size: 50,
        Cell: ({ cell }) => (
          <span style={{ color: "green", fontWeight: "bold" }}>
            {cell.row.original.Balance_instock != null
              ? formatNumber(cell.row.original.Balance_instock)
              : "N/A"}{" "}
            {cell.row.original.name ?? ""}
          </span>
        ),
      },
      {
        accessorKey: "remarks",
        header: "Notes",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.remarks || "N/A";
        },
      },
      {
        accessorKey: "selling_price",
        header: "Selling Price",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.selling_price
            ? `${formatAED(cell.row.original.selling_price)}`
            : "N/A";
        },
      },
      {
        accessorKey: "total_value",
        header: "Total Price",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.total_value
            ? `${formatAED(cell.row.original.total_value)}`
            : "N/A";
        },
      },
      {
        accessorKey: "performed_by",
        header: "Performed By",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.performed_by || "N/A";
        },
      },
    ],
    [],
  );

  return (
    <Layout>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000} // hide after 3 seconds
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#28a745",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Invoice History
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Invoice History</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} textAlign="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
              sx={{ ml: 2 }}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <Grid>
          <MaterialReactTable
            columns={columns}
            data={transactionData}
            enableStickyHeader
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            enableRowNumbers
            // className="mat-react-tbl"
            state={{
              isLoading: loading,
              pagination: { pageIndex, pageSize },
              sorting,
              columnFilters: filters,
            }}
            manualPagination
            manualSorting
            manualFiltering
            rowCount={totalCount}
            onPaginationChange={(updater) => {
              const paginationState = updater({
                pageIndex,
                pageSize,
              });

              const { pageIndex: newPageIndex, pageSize: newPageSize } =
                paginationState;
              setPageIndex(newPageIndex);
              setPageSize(newPageSize);
            }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setFilters}
            muiTableHeadCellProps={{
              sx: {
                padding: "8px",
                border: "1px solid #ccc",
                color: "#fff !important",
                fontWeight: "bold",
                backgroundColor: "#1976d2",
                "& svg": {
                  color: "#fff !important",
                },
                "& .MuiTableSortLabel-root": {
                  color: "#fff !important",
                  "&.Mui-active": {
                    color: "#fff !important",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff !important",
                    },
                  },
                },
                "& .MuiTableSortLabel-icon": {
                  color: "#fff !important",
                },
                "& .MuiInputBase-input": {
                  color: "#fff !important",
                  textFillColor: "#fff !important",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#fff !important",
                  opacity: 0.8,
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important",
                },
              },
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 240px)",
                minHeight: 450,
                overflowY: "auto",
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                backgroundColor: row.index % 2 === 0 ? "#fffff" : "#edf8fd",
              },
            })}
            muiTableBodyCellProps={{
              sx: {
                sx: {
                  padding: "3px 8px",
                  border: "1px solid #ccc",
                },
              },
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    p: "0.5rem",
                    justifyContent: "space-between",
                    flexGrow: 1,

                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* RIGHT SIDE BUTTON */}
                    <Tooltip title="Export data to excel sheet" arrow>
                      <Button
                        onClick={handleExportData}
                        style={{ background: "#1a73e829" }}
                        startIcon={<FileDownload />}
                        // startIcon={<IconTableExport />}
                      >
                        Export To Excel
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}
          />
        </Grid>
      </Grid>
    </Layout>
  );
}

export default TransactionHistory;
